import { useMutation, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { BookingPauseDuration } from "@/gql/graphql";
import {
  GET_SALON_PAUSE_STATE,
  PAUSE_ONLINE_BOOKING,
  RESUME_ONLINE_BOOKING,
} from "@/graphql/salon";
import { useSalonStore } from "@/lib/store/salon";

export interface PauseInput {
  duration: BookingPauseDuration;
  untilDate?: Date;
  message?: string;
}

/**
 * Reads and mutates the salon's online-booking pause state. `bookingPausedUntil`
 * is already sentinel-sanitized server-side, so callers render "indefinitely"
 * from `bookingPausedIndefinitely` and never a raw far-future date.
 */
export function usePauseOnlineBooking() {
  const { currentSalon } = useSalonStore();
  const { t } = useTranslation("settings");
  const salonId = currentSalon?.id ?? "";

  const { data, loading, refetch } = useQuery(GET_SALON_PAUSE_STATE, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-and-network",
  });

  const [pauseMutation, { loading: pausing }] = useMutation(PAUSE_ONLINE_BOOKING);
  const [resumeMutation, { loading: resuming }] = useMutation(RESUME_ONLINE_BOOKING);

  const salon = data?.getSalon;
  const salonTimezone = currentSalon?.timezone ?? "UTC";

  const pause = useCallback(
    async (input: PauseInput) => {
      if (!salonId) return;
      // react-day-picker returns a native JS Date at BROWSER-local midnight, but
      // Luxon's default zone is the salon's (global dashboard rule) — so reading
      // the instant via Luxon would re-zone it and slide the day. Take the picked
      // wall-clock day via native getters and build it at midnight in the salon zone.
      const picked = input.untilDate;
      const untilDate = picked
        ? DateTime.fromObject(
            { year: picked.getFullYear(), month: picked.getMonth() + 1, day: picked.getDate() },
            { zone: salonTimezone }
          ).toISO()
        : null;
      try {
        await pauseMutation({
          variables: {
            salonId,
            input: {
              duration: input.duration,
              // DateTime scalar is serialized as an ISO string by codegen config.
              untilDate,
              message: input.message?.trim() ? input.message.trim() : null,
            },
          },
        });
        await refetch();
        toast.success(t("pauseBookings.pausedSuccess"));
      } catch (err) {
        toast.error(t("pauseBookings.pauseFailed"));
        throw err;
      }
    },
    [salonId, pauseMutation, refetch, salonTimezone, t]
  );

  const resume = useCallback(async () => {
    if (!salonId) return;
    try {
      await resumeMutation({ variables: { salonId } });
      await refetch();
      toast.success(t("pauseBookings.resumedSuccess"));
    } catch (err) {
      toast.error(t("pauseBookings.resumeFailed"));
      throw err;
    }
  }, [salonId, resumeMutation, refetch, t]);

  return {
    loading,
    saving: pausing || resuming,
    isPaused: salon?.bookingPaused ?? false,
    isIndefinite: salon?.bookingPausedIndefinitely ?? false,
    pausedUntil: salon?.bookingPausedUntil ?? null,
    pausedMessage: salon?.bookingPausedMessage ?? null,
    salonTimezone,
    pause,
    resume,
  };
}
